import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  IconButton,
  Stack,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Autocomplete,
  LinearProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useSnackbar } from 'notistack';
import cuentaCorrienteService, {
  CuentaCorrienteSaldo,
  ClienteDeudaPorVencimiento,
  type EstadisticasCuentaCorriente,
  type SaldosSortBy,
  type SaldosSortDir
} from '../../services/cuentaCorrienteService';
import clienteService from '../../services/clienteService';
import rutaService, { Ruta } from '../../services/rutaService';
import VendedorSelect from '../../components/VendedorSelect';
import { VendedorLike, formatVendedorLabel } from '../../utils/vendedorUtils';
import {
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Search as SearchIcon,
  Paid as PaidIcon,
  Reply as ReplyIcon,
  ListAlt as ListAltIcon,
  ArrowBack as ArrowBackIcon,
  DateRange as DateRangeIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { navigateWithModuleBack } from '../../utils/moduleNavigationContext';
import { formatCurrency } from '../../utils/formatters';
import PDFViewer from '../../components/shared/PDFViewer';
import { searchableSelectAutocompleteProps } from '../../components/SearchableSelect';
import { getDashboardResumen } from '../../services/dashboardService';
import { useRegisterPageTour } from '../../components/tour/PageTourContext';
import { CUENTAS_COBRAR_TOUR } from '../../config/tours/informesVentasTour';
import { buildSaldoPorZonaPdfBlob, exportSaldoPorZonaExcel } from '../../utils/saldoPorZonaListado';

const CUENTAS_COBRAR_PATH = '/ventas/informes/cuentas-cobrar';
const RESUMEN_CUENTA_FROM_STATE = {
  from: CUENTAS_COBRAR_PATH,
  fromLabel: 'Cuentas por Cobrar',
} as const;

const emptyEstadisticas: EstadisticasCuentaCorriente = {
  totalClientesConCuentaCorriente: 0,
  saldoTotalGeneral: 0,
  saldoVencidoTotal: 0,
  totalMovimientos: 0,
  ultimaActualizacion: '',
  nroVendedorFiltrado: null,
  usuarioFiltrado: null,
};

type OrdenSaldosValue =
  | 'nombre-asc'
  | 'monto-desc'
  | 'monto-asc'
  | 'longevidad-desc'
  | 'longevidad-asc';

const ORDEN_SALDOS_OPTIONS: { value: OrdenSaldosValue; label: string }[] = [
  { value: 'nombre-asc', label: 'Nombre (A-Z)' },
  { value: 'monto-desc', label: 'Monto: mayor a menor' },
  { value: 'monto-asc', label: 'Monto: menor a mayor' },
  { value: 'longevidad-desc', label: 'Longevidad de deuda: mayor a menor' },
  { value: 'longevidad-asc', label: 'Longevidad de deuda: menor a mayor' }
];

const parseOrdenSaldos = (value: OrdenSaldosValue): { sortBy: SaldosSortBy; sortDir: SaldosSortDir } => {
  const [sortBy, sortDir] = value.split('-') as [SaldosSortBy, SaldosSortDir];
  return { sortBy, sortDir };
};

async function buildListadoDeudaPdfBlob(
  rows: ClienteDeudaPorVencimiento[],
  opts: {
    fechaHasta: string;
    ruta: Ruta | null;
    vendedor: VendedorLike | null;
    saldoMayorA?: number | null;
  }
): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  doc.setLineHeightFactor(1);
  const marginX = 36;
  let y = 44;
  doc.setFontSize(16);
  doc.text('Deuda por vencimiento', marginX, y);
  y += 20;
  doc.setFontSize(11);
  const hastaLabel = new Date(`${opts.fechaHasta}T12:00:00`).toLocaleDateString('es-AR', { dateStyle: 'long' });
  doc.text(`Cuentas por cobrar — vencimientos hasta ${hastaLabel}`, marginX, y);
  y += 16;
  const partes: string[] = [];
  if (opts.ruta) {
    partes.push(`Ruta: ${opts.ruta.nombreRuta ?? opts.ruta.codigoRuta ?? ''}`);
  }
  if (opts.vendedor) {
    partes.push(`Vendedor: ${formatVendedorLabel(opts.vendedor)}`);
  }
  if (opts.saldoMayorA != null && Number.isFinite(opts.saldoMayorA)) {
    partes.push(`Saldos mayores a: ${formatCurrency(opts.saldoMayorA)}`);
  }
  if (partes.length) {
    doc.setFontSize(10);
    doc.text(partes.join('  ·  '), marginX, y);
    y += 14;
  }
  doc.setFontSize(9);
  doc.text(`Generado: ${new Date().toLocaleString('es-AR')}`, marginX, y);
  y += 18;

  const totalGeneral = rows.reduce(
    (sum, r) => sum + (Number(r.deudaHastaVencimiento) || 0),
    0
  );

  const ROW_HEIGHT_SCALE = 1.15;
  const bodyFontSize = 6.5;
  const cellPadBody = {
    top: (bodyFontSize * (ROW_HEIGHT_SCALE - 1)) / 2,
    right: 2,
    bottom: (bodyFontSize * (ROW_HEIGHT_SCALE - 1)) / 2,
    left: 2
  };
  const cellPadHead = {
    top: 1 * ROW_HEIGHT_SCALE,
    right: 2,
    bottom: 1 * ROW_HEIGHT_SCALE,
    left: 2
  };

  autoTable(doc, {
    startY: y,
    theme: 'striped',
    head: [['Nro.', 'Cliente', 'Teléfono', 'Dirección', 'Ruta', 'Vendedor', 'Deuda']],
    body: rows.map((r) => [
      String(r.nroCliente),
      r.nombre,
      (r.telefono?.trim() || '—') as string,
      (r.direccion?.trim() || '—') as string,
      (r.nombreRuta ?? r.codigoRuta ?? '—') as string,
      (r.nombreVendedor ?? r.nroVendedor ?? '—') as string,
      formatCurrency(r.deudaHastaVencimiento)
    ]),
    ...(rows.length
      ? {
          foot: [
            [
              {
                content: `Totales — ${rows.length} fila${rows.length === 1 ? '' : 's'}`,
                colSpan: 6,
                styles: { halign: 'right', fontStyle: 'bold' as const }
              },
              {
                content: formatCurrency(totalGeneral),
                styles: { halign: 'right' as const, fontStyle: 'bold' as const }
              }
            ]
          ],
          footStyles: {
            fillColor: [240, 245, 250] as [number, number, number],
            textColor: [0, 0, 0] as [number, number, number],
            fontSize: 7,
            cellPadding: {
              top: 2 * ROW_HEIGHT_SCALE,
              right: 2,
              bottom: 2 * ROW_HEIGHT_SCALE,
              left: 2
            }
          },
          showFoot: 'lastPage' as const
        }
      : { showFoot: 'never' as const }),
    margin: { left: marginX, right: marginX },
    styles: {
      fontSize: bodyFontSize,
      cellPadding: cellPadBody,
      overflow: 'linebreak',
      valign: 'middle',
      minCellHeight: bodyFontSize * ROW_HEIGHT_SCALE
    },
    headStyles: {
      fillColor: [25, 118, 210],
      textColor: 255,
      fontSize: 7,
      cellPadding: cellPadHead,
      lineWidth: 0,
      valign: 'middle'
    },
    bodyStyles: {
      valign: 'middle'
    },
    columnStyles: {
      0: { cellWidth: 36 },
      1: { cellWidth: 120 },
      2: { cellWidth: 72 },
      3: { cellWidth: 140 },
      4: { cellWidth: 70 },
      5: { cellWidth: 70 },
      6: { halign: 'right', cellWidth: 68 }
    }
  });

  const totalPaginas = doc.getNumberOfPages();
  const anchoPag = doc.internal.pageSize.getWidth();
  const altoPag = doc.internal.pageSize.getHeight();
  const yPie = altoPag - 18;
  for (let p = 1; p <= totalPaginas; p += 1) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text(`Página ${p} de ${totalPaginas}`, anchoPag / 2, yPie, { align: 'center' });
  }
  doc.setTextColor(0, 0, 0);

  return doc.output('blob');
}

const LISTADOS = [
  {
    id: 'deudaVencida' as const,
    titulo: 'Deuda vencida a fecha',
    descripcion:
      'Clientes con saldo en cuenta corriente por vencimientos hasta una fecha, filtrable por ruta y vendedor.'
  },
  {
    id: 'saldoPorZona' as const,
    titulo: 'Saldo por zona a fecha',
    descripcion:
      'Resumen de cuenta de clientes agrupado por zona: saldo anterior, movimientos, saldo por cliente y total por zona a una fecha.'
  }
] as const;

type ListadoId = (typeof LISTADOS)[number]['id'];

const primerDiaDelMes = (): string => {
  const hoy = new Date();
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`;
};

interface VendedorClienteInfo {
  nroVendedor?: number;
  nombreVendedor?: string | null;
}

const CuentasPorCobrar: React.FC = () => {
  useRegisterPageTour(CUENTAS_COBRAR_TOUR);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [saldos, setSaldos] = useState<CuentaCorrienteSaldo[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [nroVendedorFiltro, setNroVendedorFiltro] = useState<number | null>(null);
  const [vendedorFiltro, setVendedorFiltro] = useState<VendedorLike | null>(null);
  const [ordenSaldos, setOrdenSaldos] = useState<OrdenSaldosValue>('nombre-asc');
  const [exportProgress, setExportProgress] = useState({ message: '', currentPage: 0, totalPages: 0 });
  const clienteVendedorMapRef = useRef<Map<number, VendedorClienteInfo>>(new Map());
  const [, setMapVersion] = useState(0);

  const [listadosOpen, setListadosOpen] = useState(false);
  const [listadoEmitir, setListadoEmitir] = useState<null | ListadoId>(null);
  const [fechaHastaListado, setFechaHastaListado] = useState(() => new Date().toISOString().slice(0, 10));
  const [rutaFiltro, setRutaFiltro] = useState<Ruta | null>(null);
  const [nroVendedorListadoFiltro, setNroVendedorListadoFiltro] = useState<number | null>(null);
  const [vendedorListadoFiltro, setVendedorListadoFiltro] = useState<VendedorLike | null>(null);
  const [saldoMayorAListado, setSaldoMayorAListado] = useState('');
  const [rutasOptions, setRutasOptions] = useState<Ruta[]>([]);
  const [loadingCatalogosListado, setLoadingCatalogosListado] = useState(false);
  const [_listadoDeudaRows, setListadoDeudaRows] = useState<ClienteDeudaPorVencimiento[]>([]);
  const [loadingListadoDeuda, setLoadingListadoDeuda] = useState(false);
  const [exportingListadoDeuda, setExportingListadoDeuda] = useState(false);
  const [listadoDeudaPdfUrl, setListadoDeudaPdfUrl] = useState<string | null>(null);
  const [fechaDesdeSaldoZona, setFechaDesdeSaldoZona] = useState(primerDiaDelMes);
  const [incluirMovimientosSaldoZona, setIncluirMovimientosSaldoZona] = useState(true);
  const [loadingSaldoZona, setLoadingSaldoZona] = useState(false);
  const [exportingSaldoZona, setExportingSaldoZona] = useState(false);
  const [saldoZonaPdfUrl, setSaldoZonaPdfUrl] = useState<string | null>(null);
  const [estadisticas, setEstadisticas] = useState<EstadisticasCuentaCorriente>(emptyEstadisticas);
  const [loadingKpis, setLoadingKpis] = useState(false);
  const [cobrosMesActual, setCobrosMesActual] = useState(0);
  const [diasPromedioCobro, setDiasPromedioCobro] = useState<number | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const goToResumenCuenta = (nroCliente: number) => {
    navigate(`/ventas/informes/resumen-cuenta/${nroCliente}`, {
      state: RESUMEN_CUENTA_FROM_STATE,
    });
  };

  const enrichVendedoresClientes = useCallback(async (nroClientes: number[]) => {
    const missing = nroClientes.filter((n) => !clienteVendedorMapRef.current.has(n));
    if (!missing.length) return;

    const entries = await Promise.all(
      missing.map(async (nroCliente) => {
        try {
          const cliente = await clienteService.getCliente(nroCliente);
          return [
            nroCliente,
            {
              nroVendedor: cliente.vendedor ?? cliente.vendedorInfo?.nroVendedor,
              nombreVendedor: cliente.vendedorInfo?.nombreVendedor
            }
          ] as const;
        } catch {
          return [nroCliente, {}] as const;
        }
      })
    );

    for (const [nro, info] of entries) {
      clienteVendedorMapRef.current.set(nro, info);
    }
    setMapVersion((v) => v + 1);
  }, []);

  const getVendedorLabel = useCallback((nroCliente: number): string => {
    if (vendedorFiltro) {
      return formatVendedorLabel(vendedorFiltro);
    }

    const info = clienteVendedorMapRef.current.get(nroCliente);
    if (!info?.nroVendedor) return '-';
    return formatVendedorLabel(info as VendedorLike);
  }, [vendedorFiltro]);

  useEffect(() => {
    if (!listadosOpen || listadoEmitir !== 'deudaVencida') return;
    let cancelled = false;
    const loadCatalogos = async () => {
      setLoadingCatalogosListado(true);
      try {
        const pageSize = 20;
        const rutas: Ruta[] = [];
        let p = 1;
        while (!cancelled) {
          const r = await rutaService.getRutasPaged({ pageNumber: p, pageSize });
          rutas.push(...r.items);
          if (p >= r.totalPages || r.items.length === 0) break;
          p += 1;
        }
        if (!cancelled) {
          setRutasOptions(rutas);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          enqueueSnackbar('No se pudieron cargar rutas para el listado.', { variant: 'error' });
        }
      } finally {
        if (!cancelled) setLoadingCatalogosListado(false);
      }
    };
    void loadCatalogos();
    return () => {
      cancelled = true;
    };
  }, [listadosOpen, listadoEmitir, enqueueSnackbar]);

  const handleCloseListadoDeudaPdf = () => {
    setListadoDeudaPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const parseSaldoMayorA = (): number | null => {
    const raw = saldoMayorAListado.trim().replace(',', '.');
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return null;
    return n;
  };

  const filtrarDeudaPorSaldoMayorA = (
    rows: ClienteDeudaPorVencimiento[],
    minimo: number | null
  ): ClienteDeudaPorVencimiento[] => {
    if (minimo == null) return rows;
    return rows.filter((r) => (Number(r.deudaHastaVencimiento) || 0) > minimo);
  };

  const handleGenerarListadoDeuda = async () => {
    if (!fechaHastaListado) {
      enqueueSnackbar('Indicá la fecha hasta de vencimiento.', { variant: 'warning' });
      return;
    }
    const saldoMayorA = parseSaldoMayorA();
    if (saldoMayorAListado.trim() !== '' && saldoMayorA == null) {
      enqueueSnackbar('El monto de “Incluir saldos mayores a” no es válido.', { variant: 'warning' });
      return;
    }
    try {
      setLoadingListadoDeuda(true);
      const rowsRaw = await cuentaCorrienteService.getDeudaPorVencimiento({
        fechaHasta: fechaHastaListado,
        codigoRuta: rutaFiltro?.codigoRuta ?? undefined,
        nroVendedor: nroVendedorListadoFiltro ?? undefined
      });
      const rows = filtrarDeudaPorSaldoMayorA(rowsRaw, saldoMayorA);
      setListadoDeudaRows(rows);
      if (!rows.length) {
        enqueueSnackbar('No hay clientes con deuda en ese corte.', { variant: 'warning' });
        return;
      }
      const blob = await buildListadoDeudaPdfBlob(rows, {
        fechaHasta: fechaHastaListado,
        ruta: rutaFiltro,
        vendedor: vendedorListadoFiltro,
        saldoMayorA
      });
      setListadoDeudaPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      setListadoEmitir(null);
      setListadosOpen(false);
      enqueueSnackbar(`PDF generado: ${rows.length} cliente(s).`, { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('No se pudo generar el listado de deuda por vencimiento.', { variant: 'error' });
    } finally {
      setLoadingListadoDeuda(false);
    }
  };

  const writeListadoDeudaToExcel = (rows: ClienteDeudaPorVencimiento[], fechaCorte: string) => {
    const data: Array<Record<string, string | number>> = rows.map((row) => ({
      'Nro. Cliente': row.nroCliente,
      Nombre: row.nombre,
      Teléfono: row.telefono?.trim() || '',
      Dirección: row.direccion?.trim() || '',
      'Cód. Ruta': row.codigoRuta ?? '',
      Ruta: row.nombreRuta ?? '',
      'Nro. Vendedor': row.nroVendedor ?? '',
      Vendedor: row.nombreVendedor ?? '',
      'Deuda venc. hasta fecha': row.deudaHastaVencimiento
    }));
    if (rows.length) {
      const totalGeneral = rows.reduce(
        (sum, r) => sum + (Number(r.deudaHastaVencimiento) || 0),
        0
      );
      data.push({
        'Nro. Cliente': '',
        Nombre: `Total de filas: ${rows.length}`,
        Teléfono: '',
        Dirección: '',
        'Cód. Ruta': '',
        Ruta: '',
        'Nro. Vendedor': '',
        Vendedor: '',
        'Deuda venc. hasta fecha': ''
      });
      data.push({
        'Nro. Cliente': '',
        Nombre: 'Total general',
        Teléfono: '',
        Dirección: '',
        'Cód. Ruta': '',
        Ruta: '',
        'Nro. Vendedor': '',
        Vendedor: '',
        'Deuda venc. hasta fecha': totalGeneral
      });
    }
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'DeudaPorVencimiento');
    const slug = fechaCorte.replace(/-/g, '');
    XLSX.writeFile(workbook, `deuda_clientes_por_vencimiento_${slug}.xlsx`);
  };

  const handleExportListadoDeudaExcel = async () => {
    if (!fechaHastaListado) {
      enqueueSnackbar('Indicá la fecha hasta de vencimiento para exportar.', { variant: 'warning' });
      return;
    }
    const saldoMayorA = parseSaldoMayorA();
    if (saldoMayorAListado.trim() !== '' && saldoMayorA == null) {
      enqueueSnackbar('El monto de “Incluir saldos mayores a” no es válido.', { variant: 'warning' });
      return;
    }
    try {
      setExportingListadoDeuda(true);
      const rowsRaw = await cuentaCorrienteService.getDeudaPorVencimiento({
        fechaHasta: fechaHastaListado,
        codigoRuta: rutaFiltro?.codigoRuta ?? undefined,
        nroVendedor: nroVendedorListadoFiltro ?? undefined
      });
      const rows = filtrarDeudaPorSaldoMayorA(rowsRaw, saldoMayorA);
      if (!rows.length) {
        enqueueSnackbar('No hay clientes con deuda en ese corte para exportar.', { variant: 'warning' });
        return;
      }
      setListadoDeudaRows(rows);
      writeListadoDeudaToExcel(rows, fechaHastaListado);
      enqueueSnackbar('Exportación completada.', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('No se pudo exportar el listado a Excel.', { variant: 'error' });
    } finally {
      setExportingListadoDeuda(false);
    }
  };

  const fetchSaldoPorZona = async () => {
    if (!fechaDesdeSaldoZona || !fechaHastaListado) {
      enqueueSnackbar('Indicá la fecha desde y la fecha del saldo.', { variant: 'warning' });
      return null;
    }
    if (fechaDesdeSaldoZona > fechaHastaListado) {
      enqueueSnackbar('La fecha desde no puede ser posterior a la fecha del saldo.', { variant: 'warning' });
      return null;
    }
    const informe = await cuentaCorrienteService.getSaldoPorZona({
      fechaDesde: fechaDesdeSaldoZona,
      fechaHasta: fechaHastaListado,
      incluirMovimientos: incluirMovimientosSaldoZona
    });
    if (!informe.zonas.length) {
      enqueueSnackbar('No hay clientes con saldo o movimientos a esa fecha.', { variant: 'warning' });
      return null;
    }
    return informe;
  };

  const saldoZonaOpts = () => ({ incluirMovimientos: incluirMovimientosSaldoZona });

  const handleGenerarSaldoPorZona = async () => {
    try {
      setLoadingSaldoZona(true);
      const informe = await fetchSaldoPorZona();
      if (!informe) return;
      const blob = await buildSaldoPorZonaPdfBlob(informe, saldoZonaOpts());
      setSaldoZonaPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      setListadoEmitir(null);
      setListadosOpen(false);
      const clientes = informe.zonas.reduce((n, z) => n + z.clientes.length, 0);
      enqueueSnackbar(`PDF generado: ${informe.zonas.length} zona(s), ${clientes} cliente(s).`, { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('No se pudo generar el listado de saldo por zona.', { variant: 'error' });
    } finally {
      setLoadingSaldoZona(false);
    }
  };

  const handleExportSaldoPorZonaExcel = async () => {
    try {
      setExportingSaldoZona(true);
      const informe = await fetchSaldoPorZona();
      if (!informe) return;
      exportSaldoPorZonaExcel(informe, saldoZonaOpts());
      enqueueSnackbar('Exportación completada.', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('No se pudo exportar el listado de saldo por zona a Excel.', { variant: 'error' });
    } finally {
      setExportingSaldoZona(false);
    }
  };

  const handleCloseSaldoZonaPdf = () => {
    setSaldoZonaPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleCloseListados = () => {
    setListadoEmitir(null);
    setListadosOpen(false);
  };

  const handleOpenListados = () => {
    setListadoEmitir(null);
    setListadoDeudaPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    handleCloseSaldoZonaPdf();
    setListadosOpen(true);
  };

  // Debounce para la búsqueda y carga de datos
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const { sortBy, sortDir } = parseOrdenSaldos(ordenSaldos);
        const params: {
          pageNumber: number;
          pageSize: number;
          searchTerm?: string;
          nroVendedor?: number;
          sortBy: SaldosSortBy;
          sortDir: SaldosSortDir;
        } = {
          pageNumber: page + 1,
          pageSize: rowsPerPage,
          sortBy,
          sortDir
        };

        // Agregar searchTerm solo si tiene contenido
        if (searchTerm.trim()) {
          params.searchTerm = searchTerm.trim();
        }

        if (nroVendedorFiltro) {
          params.nroVendedor = nroVendedorFiltro;
        }

        const response = await cuentaCorrienteService.getSaldos(params);

        setSaldos(response.items);
        setTotalCount(response.totalCount);
        if (response.items.length > 0) {
          enrichVendedoresClientes(response.items.map((item) => item.nroCliente));
        }
      } catch (error) {
        console.error('Error al obtener las cuentas por cobrar:', error);
        enqueueSnackbar('No se pudieron cargar los saldos de clientes.', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      loadData();
    }, searchTerm.trim() ? 500 : 0); // Debounce solo para búsqueda, carga inmediata para paginación

    return () => clearTimeout(timer);
  }, [searchTerm, nroVendedorFiltro, ordenSaldos, page, rowsPerPage, enqueueSnackbar, enrichVendedoresClientes]);

  useEffect(() => {
    let cancelled = false;
    const loadKpis = async () => {
      setLoadingKpis(true);
      try {
        const [stats, resumen] = await Promise.all([
          cuentaCorrienteService.getEstadisticas({
            nroVendedor: nroVendedorFiltro,
          }),
          getDashboardResumen(),
        ]);
        if (cancelled) return;
        setEstadisticas(stats);
        setCobrosMesActual(Number(resumen.totalCobradoMes) || 0);
        setDiasPromedioCobro(
          resumen.diasPromedioPagoCobranzas != null
            ? Number(resumen.diasPromedioPagoCobranzas)
            : null
        );
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          enqueueSnackbar('No se pudieron cargar los indicadores de cuentas por cobrar.', {
            variant: 'warning',
          });
        }
      } finally {
        if (!cancelled) setLoadingKpis(false);
      }
    };
    void loadKpis();
    return () => {
      cancelled = true;
    };
  }, [nroVendedorFiltro, enqueueSnackbar]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleVendedorFiltroChange = (nroVendedor: number | null, vendedor?: VendedorLike | null) => {
    setNroVendedorFiltro(nroVendedor);
    setVendedorFiltro(vendedor ?? null);
    setPage(0);
  };

  const handleOrdenSaldosChange = (event: SelectChangeEvent<OrdenSaldosValue>) => {
    setOrdenSaldos(event.target.value as OrdenSaldosValue);
    setPage(0);
  };

  // Función auxiliar para obtener todos los datos paginados
  const getAllSaldos = async (onProgress?: (current: number, total: number) => void): Promise<CuentaCorrienteSaldo[]> => {
    const allItems: CuentaCorrienteSaldo[] = [];
    const pageSize = 50; // Tamaño de página para obtener datos (optimizado)
    let pageNumber = 1;
    let hasMore = true;
    let totalPages = 0;
    const { sortBy, sortDir } = parseOrdenSaldos(ordenSaldos);

    const params: {
      pageNumber: number;
      pageSize: number;
      searchTerm?: string;
      nroVendedor?: number;
      sortBy: SaldosSortBy;
      sortDir: SaldosSortDir;
    } = {
      pageNumber,
      pageSize,
      sortBy,
      sortDir
    };

    // Incluir searchTerm si existe
    if (searchTerm.trim()) {
      params.searchTerm = searchTerm.trim();
    }

    if (nroVendedorFiltro) {
      params.nroVendedor = nroVendedorFiltro;
    }

    while (hasMore) {
      const response = await cuentaCorrienteService.getSaldos({
        ...params,
        pageNumber
      });

      allItems.push(...response.items);

      // Verificar si hay más páginas
      totalPages = Math.ceil(response.totalCount / pageSize);
      hasMore = pageNumber < totalPages;
      
      // Notificar progreso
      if (onProgress) {
        onProgress(pageNumber, totalPages);
      }
      
      pageNumber++;
    }

    return allItems;
  };

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExporting(true);
      setExportProgress({ message: 'Obteniendo datos...', currentPage: 0, totalPages: 0 });
      
      const allItems = await getAllSaldos((current, total) => {
        setExportProgress({ 
          message: `Obteniendo datos (${current} de ${total} páginas)...`, 
          currentPage: current, 
          totalPages: total 
        });
      });

      if (!allItems.length) {
        setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
        enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
        return;
      }

      setExportProgress({ message: 'Obteniendo vendedores de clientes...', currentPage: 0, totalPages: 0 });
      await enrichVendedoresClientes(allItems.map((item) => item.nroCliente));

      setExportProgress({ message: 'Generando archivo Excel...', currentPage: 0, totalPages: 0 });

      const data = allItems.map((item) => ({
        'Nro. Cliente': item.nroCliente,
        Nombre: item.nombre,
        Vendedor: getVendedorLabel(item.nroCliente),
        Teléfono: item.telefono || '-',
        'Saldo Total': item.saldoTotal,
        'Saldo Vigente': item.saldoVigente,
        'Saldo Vencido': item.saldoVencido
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'CuentasPorCobrar');

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `cuentas_por_cobrar_${today}.xlsx`);
      
      setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
      enqueueSnackbar('Exportación completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay información para compartir.', { variant: 'warning' });
      return;
    }

    try {
      setExportingPdf(true);
      setExportProgress({ message: 'Obteniendo datos...', currentPage: 0, totalPages: 0 });
      
      const allItems = await getAllSaldos((current, total) => {
        setExportProgress({ 
          message: `Obteniendo datos (${current} de ${total} páginas)...`, 
          currentPage: current, 
          totalPages: total 
        });
      });

      if (!allItems.length) {
        setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
        enqueueSnackbar('No hay información para compartir.', { variant: 'warning' });
        return;
      }

      setExportProgress({ message: 'Obteniendo vendedores de clientes...', currentPage: 0, totalPages: 0 });
      await enrichVendedoresClientes(allItems.map((item) => item.nroCliente));

      setExportProgress({ message: 'Generando archivo PDF...', currentPage: 0, totalPages: 0 });

      const { jsPDF } = await import('jspdf');

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const marginX = 36;
      let currentY = 44;

      doc.setFontSize(16);
      doc.text('Cuentas por Cobrar', marginX, currentY);
      currentY += 18;

      doc.setFontSize(9);
      const today = new Date();
      doc.text(`Generado: ${today.toLocaleString('es-AR')}`, marginX, currentY);
      currentY += 14;

      if (nroVendedorFiltro && vendedorFiltro) {
        doc.setFontSize(9);
        doc.text(`Vendedor: ${formatVendedorLabel(vendedorFiltro)}`, marginX, currentY);
        currentY += 12;
      }

      const totalSaldo = allItems.reduce((s, r) => s + (Number(r.saldoTotal) || 0), 0);
      const totalVigente = allItems.reduce((s, r) => s + (Number(r.saldoVigente) || 0), 0);
      const totalVencido = allItems.reduce((s, r) => s + (Number(r.saldoVencido) || 0), 0);

      const kpiDeuda =
        estadisticas.saldoTotalGeneral != null
          ? estadisticas.saldoTotalGeneral
          : totalSaldo;
      const kpiClientes =
        estadisticas.totalClientesConCuentaCorriente > 0
          ? estadisticas.totalClientesConCuentaCorriente
          : allItems.length;
      const kpiDias =
        diasPromedioCobro != null ? `${diasPromedioCobro.toFixed(1)} días` : '—';

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Resumen', marginX, currentY);
      currentY += 11;
      doc.setFont('helvetica', 'normal');
      const kpiLineas = [
        `Deuda total: ${formatCurrency(kpiDeuda)}`,
        `Prom. días de cobro (mes): ${kpiDias}`,
        `Clientes con deuda: ${kpiClientes.toLocaleString('es-AR')}`,
        `Cobros del mes actual: ${formatCurrency(cobrosMesActual)}`,
      ];
      for (const linea of kpiLineas) {
        doc.text(linea, marginX, currentY);
        currentY += 10;
      }
      currentY += 6;

      const autoTable = (await import('jspdf-autotable')).default;
      const bodyFontSize = 7;
      const cellPad = { top: 1.5, right: 2, bottom: 1.5, left: 2 };

      autoTable(doc, {
        startY: currentY,
        theme: 'striped',
        head: [['Nro.', 'Cliente', 'Vendedor', 'Teléfono', 'Saldo Total', 'Vigente', 'Vencido']],
        body: allItems.map((row) => [
          String(row.nroCliente),
          row.nombre,
          getVendedorLabel(row.nroCliente),
          row.telefono?.trim() || '—',
          formatCurrency(row.saldoTotal),
          formatCurrency(row.saldoVigente),
          formatCurrency(row.saldoVencido),
        ]),
        foot: [
          [
            {
              content: `Totales — ${allItems.length} fila${allItems.length === 1 ? '' : 's'}`,
              colSpan: 4,
              styles: { halign: 'right', fontStyle: 'bold' as const },
            },
            {
              content: formatCurrency(totalSaldo),
              styles: { halign: 'right' as const, fontStyle: 'bold' as const },
            },
            {
              content: formatCurrency(totalVigente),
              styles: { halign: 'right' as const, fontStyle: 'bold' as const },
            },
            {
              content: formatCurrency(totalVencido),
              styles: { halign: 'right' as const, fontStyle: 'bold' as const },
            },
          ],
        ],
        showFoot: 'lastPage',
        margin: { left: marginX, right: marginX },
        styles: {
          fontSize: bodyFontSize,
          cellPadding: cellPad,
          overflow: 'linebreak',
          valign: 'middle',
          minCellHeight: bodyFontSize + 2,
        },
        headStyles: {
          fillColor: [25, 118, 210],
          textColor: 255,
          fontSize: 7,
          cellPadding: cellPad,
          valign: 'middle',
        },
        footStyles: {
          fillColor: [240, 245, 250],
          textColor: [0, 0, 0],
          fontSize: 7,
          cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
        },
        columnStyles: {
          0: { cellWidth: 32 },
          1: { cellWidth: 130 },
          2: { cellWidth: 90 },
          3: { cellWidth: 62 },
          4: { halign: 'right', cellWidth: 68 },
          5: { halign: 'right', cellWidth: 62 },
          6: { halign: 'right', cellWidth: 62 },
        },
      });

      const totalPaginas = doc.getNumberOfPages();
      const anchoPag = doc.internal.pageSize.getWidth();
      const altoPag = doc.internal.pageSize.getHeight();
      for (let p = 1; p <= totalPaginas; p += 1) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(90, 90, 90);
        doc.text(`Página ${p} de ${totalPaginas}`, anchoPag / 2, altoPag - 18, { align: 'center' });
      }
      doc.setTextColor(0, 0, 0);

      const pdfBlob = doc.output('blob');
      const fileName = `cuentas_por_cobrar_${today.toISOString().slice(0, 10)}.pdf`;

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
      enqueueSnackbar('PDF descargado correctamente.', { variant: 'success' });
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
        enqueueSnackbar('Compartir cancelado por el usuario.', { variant: 'warning' });
      } else {
        console.error('Error al generar el PDF para compartir:', error);
        setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
        enqueueSnackbar('No se pudo generar el PDF para compartir.', { variant: 'error' });
      }
    } finally {
      setExportingPdf(false);
    }
  };

  const renderAcciones = (row: CuentaCorrienteSaldo) => (
    <Stack
      direction="row"
      spacing={0.5}
      justifyContent="center"
      {...(row === saldos[0] ? { 'data-tour-id': 'informe-fila-acciones' } : {})}
    >
      <IconButton
        size="small"
        color="primary"
        title="Cobrar"
        aria-label="Cobrar"
        onClick={(e) => {
          e.stopPropagation();
          navigateWithModuleBack(
            navigate,
            `/tesoreria/recibos/nuevo?nroCliente=${row.nroCliente}`,
            '/ventas',
          );
        }}
        sx={{
          bgcolor: 'primary.main',
          color: 'common.white',
          '&:hover': { bgcolor: 'primary.dark' },
        }}
      >
        <PaidIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color="secondary"
        title="Nueva devolución"
        aria-label="Nueva devolución"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/tesoreria/devoluciones/nuevo?nroCliente=${row.nroCliente}`);
        }}
        sx={{
          bgcolor: 'secondary.main',
          color: 'common.white',
          '&:hover': { bgcolor: 'secondary.dark' },
        }}
      >
        <ReplyIcon fontSize="small" />
      </IconButton>
    </Stack>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center">
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    if (!loading && saldos.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center">
            {searchTerm.trim() || nroVendedorFiltro ? 'No se encontraron resultados para la búsqueda.' : 'No hay clientes con saldo.'}
          </TableCell>
        </TableRow>
      );
    }

    return saldos.map((row) => (
      <TableRow 
        key={row.nroCliente} 
        onClick={() => {
          goToResumenCuenta(row.nroCliente);
        }}
        sx={{ 
          cursor: 'pointer',
          '&:hover': {
            bgcolor: '#f9f9f9',
          },
        }}
      >
        <TableCell sx={{ fontWeight: 500 }}>{row.nroCliente}</TableCell>
        <TableCell>{row.nombre}</TableCell>
        <TableCell>{getVendedorLabel(row.nroCliente)}</TableCell>
        <TableCell>{row.telefono || '-'}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
          {formatCurrency(row.saldoTotal)}
        </TableCell>
        <TableCell align="right">{formatCurrency(row.saldoVigente)}</TableCell>
        <TableCell align="right" sx={{ 
          color: row.saldoVencido > 0 ? '#f44336' : 'inherit',
          fontWeight: row.saldoVencido > 0 ? 600 : 'normal'
        }}>
          {formatCurrency(row.saldoVencido)}
        </TableCell>
        <TableCell
          align="center"
          onClick={(e) => e.stopPropagation()}
          sx={{ width: '1%', whiteSpace: 'nowrap' }}
        >
          {renderAcciones(row)}
        </TableCell>
      </TableRow>
    ));
  };

  const renderCardCampo = (label: string, valor: React.ReactNode, sx?: object) => (
    <Box sx={sx}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2">{valor}</Typography>
    </Box>
  );

  const renderCardsMobile = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (!loading && saldos.length === 0) {
      return (
        <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
          {searchTerm.trim() || nroVendedorFiltro ? 'No se encontraron resultados para la búsqueda.' : 'No hay clientes con saldo.'}
        </Typography>
      );
    }

    return (
      <Stack spacing={2}>
        {saldos.map((row) => (
          <Paper
            key={row.nroCliente}
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              cursor: 'pointer',
              '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
            }}
            onClick={() => goToResumenCuenta(row.nroCliente)}
          >
            <Stack spacing={1.5}>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  {renderCardCampo('Nro. Cliente', row.nroCliente)}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo('Nombre', row.nombre)}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo('Vendedor', getVendedorLabel(row.nroCliente))}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo('Teléfono', row.telefono || '-')}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo(
                    'Saldo Total',
                    <Box component="span" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {formatCurrency(row.saldoTotal)}
                    </Box>
                  )}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo('Saldo Vigente', formatCurrency(row.saldoVigente))}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo(
                    'Saldo Vencido',
                    <Box
                      component="span"
                      sx={{
                        color: row.saldoVencido > 0 ? '#f44336' : 'inherit',
                        fontWeight: row.saldoVencido > 0 ? 600 : 'normal',
                      }}
                    >
                      {formatCurrency(row.saldoVencido)}
                    </Box>
                  )}
                </Grid>
              </Grid>
              <Box onClick={(e) => e.stopPropagation()}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Acciones
                </Typography>
                {renderAcciones(row)}
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título y botones */}
          <Stack 
            direction="row"
            justifyContent="space-between" 
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            sx={{ mb: 3 }}
          >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/ventas/informes')}
            sx={{ textTransform: 'none' }}
          >
            Informes
          </Button>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 600,
              color: '#1976d2'
            }}
          >
            Cuentas por Cobrar
          </Typography>
        </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-tour-id="informe-acciones">
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ListAltIcon />}
            onClick={handleOpenListados}
            disabled={loading}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Listados
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            disabled={loading || exporting}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
          >
            Exportar Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleShare}
            disabled={loading || exportingPdf}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
          >
            Exportar PDF
          </Button>
        </Stack>
          </Stack>

          {/* KPI resumen */}
          <Grid container spacing={2} sx={{ mb: 3 }} data-tour-id="informe-kpis">
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#1976d2', color: '#fff', textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
                <AccountBalanceWalletIcon sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
                  {loadingKpis ? '…' : formatCurrency(estadisticas.saldoTotalGeneral)}
                </Typography>
                <Typography variant="caption">Deuda total</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#ed6c02', color: '#fff', textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
                <ScheduleIcon sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
                  {loadingKpis
                    ? '…'
                    : diasPromedioCobro != null
                      ? `${diasPromedioCobro.toFixed(1)} días`
                      : '—'}
                </Typography>
                <Typography variant="caption">Prom. días de cobro (mes)</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#9c27b0', color: '#fff', textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
                <PeopleIcon sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
                  {loadingKpis
                    ? '…'
                    : estadisticas.totalClientesConCuentaCorriente.toLocaleString('es-AR')}
                </Typography>
                <Typography variant="caption">Clientes con deuda</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#2e7d32', color: '#fff', textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
                <PaymentsIcon sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
                  {loadingKpis ? '…' : formatCurrency(cobrosMesActual)}
                </Typography>
                <Typography variant="caption">Cobros del mes actual</Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Filtros */}
          <Grid container spacing={2} sx={{ mb: 3 }} data-tour-id="informe-filtros">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Buscar por número de cliente o nombre..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <VendedorSelect
                value={nroVendedorFiltro}
                onChange={handleVendedorFiltroChange}
                allowEmpty
                emptyOptionLabel="Todos"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="orden-saldos-label">Ordenar por</InputLabel>
                <Select
                  labelId="orden-saldos-label"
                  id="orden-saldos"
                  value={ordenSaldos}
                  label="Ordenar por"
                  onChange={handleOrdenSaldosChange}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  {ORDEN_SALDOS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Tabla (desktop) / Cards (mobile) */}
          {isMobile ? (
            <Box data-tour-id="informe-resultados">
              {renderCardsMobile()}
              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => {
                  if (searchTerm.trim() || nroVendedorFiltro) {
                    return `${from}-${to} de ${count} (filtrado)`;
                  }
                  return `${from}-${to} de ${count}`;
                }}
                sx={{
                  mt: 2,
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: 1
                  }
                }}
              />
            </Box>
          ) : (
          <TableContainer 
            component={Paper}
            data-tour-id="informe-resultados"
            sx={{
              borderRadius: 2,
              boxShadow: 'none',
              border: '1px solid #e0e0e0',
            }}
          >
          <Table>
            <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Nro. Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Vendedor</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Teléfono</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Total</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Vigente</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Vencido</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderBody()}</TableBody>
          </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => {
            if (searchTerm.trim() || nroVendedorFiltro) {
              return `${from}-${to} de ${count} (filtrado)`;
            }
            return `${from}-${to} de ${count}`;
          }}
              sx={{
                borderTop: '1px solid #e0e0e0',
              }}
        />
          </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Diálogo de progreso de exportación */}
      <Dialog
        open={exporting || exportingPdf}
        aria-labelledby="export-progress-dialog"
        PaperProps={{
          sx: {
            minWidth: 300,
            p: 2
          }
        }}
      >
        <DialogContent>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={40} />
            <Typography variant="body1" align="center" sx={{ fontWeight: 500 }}>
              {exportProgress.message || 'Preparando exportación...'}
            </Typography>
            {exportProgress.totalPages > 0 && (
              <Box sx={{ width: '100%', mt: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={(exportProgress.currentPage / exportProgress.totalPages) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                  {exportProgress.currentPage} de {exportProgress.totalPages} páginas
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" align="center">
              Por favor, espere mientras se procesan los datos...
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={listadosOpen}
        onClose={handleCloseListados}
        maxWidth={listadoEmitir != null ? 'sm' : 'md'}
        fullWidth
        fullScreen={isMobile}
      >
        {listadoEmitir == null ? (
          <>
            <DialogTitle>Elegir listado</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Seleccioná qué listado emitir. Luego se abrirá la pantalla con filtros y generación.
              </Typography>
              <Stack spacing={1.5}>
                {LISTADOS.map((opt) => (
                  <Button
                    key={opt.id}
                    fullWidth
                    variant="outlined"
                    onClick={() => setListadoEmitir(opt.id)}
                    startIcon={<DateRangeIcon />}
                    sx={{
                      py: 1.5,
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      borderRadius: 2,
                      borderColor: '#e0e0e0',
                      '&:hover': { borderColor: '#1976d2', bgcolor: 'rgba(25, 118, 210, 0.04)' }
                    }}
                  >
                    <Box sx={{ display: 'block', textAlign: 'left' }}>
                      <Typography component="span" display="block" fontWeight={600} color="text.primary">
                        {opt.titulo}
                      </Typography>
                      <Typography component="span" variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {opt.descripcion}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleCloseListados} sx={{ textTransform: 'none' }}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        ) : listadoEmitir === 'deudaVencida' ? (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pr: 1
              }}
            >
              <IconButton
                size="small"
                onClick={() => setListadoEmitir(null)}
                aria-label="Volver a listados"
                edge="start"
              >
                <ArrowBackIcon />
              </IconButton>
              <Box component="span" sx={{ flex: 1 }}>
                Deuda vencida a fecha
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Clientes filtrados por ruta y/o vendedor. La deuda es la suma de saldos en cuenta corriente con fecha de
                vencimiento hasta la fecha indicada (solo comprobantes con vencimiento informado). No se listan clientes
                con deuda 0. Al imprimir, se abre el PDF en el visor integrado; podés volver a abrir Listados.
              </Typography>
              <Stack spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="Fecha hasta (vencimiento)"
                  type="date"
                  value={fechaHastaListado}
                  onChange={(e) => setFechaHastaListado(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
                <Autocomplete
                  {...searchableSelectAutocompleteProps}
                  options={rutasOptions}
                  value={rutaFiltro}
                  onChange={(_, v) => setRutaFiltro(v)}
                  getOptionLabel={(o) => (o?.nombreRuta != null ? `${o.codigoRuta ?? ''} — ${o.nombreRuta}` : '')}
                  isOptionEqualToValue={(a, b) => (a?.codigoRuta ?? null) === (b?.codigoRuta ?? null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Ruta (zona)" placeholder="Todas" size="small" />
                  )}
                  disabled={loadingCatalogosListado}
                />
                <VendedorSelect
                  value={nroVendedorListadoFiltro}
                  onChange={(nro, vendedor) => {
                    setNroVendedorListadoFiltro(nro);
                    setVendedorListadoFiltro(vendedor ?? null);
                  }}
                  allowEmpty
                  emptyOptionLabel="Todos"
                  size="small"
                />
                <TextField
                  label="Incluir saldos mayores a"
                  type="number"
                  value={saldoMayorAListado}
                  onChange={(e) => setSaldoMayorAListado(e.target.value)}
                  placeholder="Ej: 1000"
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  helperText="Opcional. Solo incluye clientes con deuda estrictamente mayor a ese monto."
                />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    onClick={() => void handleGenerarListadoDeuda()}
                    disabled={loadingListadoDeuda || loadingCatalogosListado || exportingListadoDeuda}
                    sx={{ textTransform: 'none' }}
                  >
                    Imprimir
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FileDownloadIcon />}
                    onClick={() => void handleExportListadoDeudaExcel()}
                    disabled={exportingListadoDeuda || loadingListadoDeuda || loadingCatalogosListado}
                    sx={{ textTransform: 'none' }}
                  >
                    Exportar Excel
                  </Button>
                  {(loadingCatalogosListado || loadingListadoDeuda) && <CircularProgress size={22} />}
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleCloseListados} sx={{ textTransform: 'none' }}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        ) : listadoEmitir === 'saldoPorZona' ? (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pr: 1
              }}
            >
              <IconButton
                size="small"
                onClick={() => setListadoEmitir(null)}
                aria-label="Volver a listados"
                edge="start"
              >
                <ArrowBackIcon />
              </IconButton>
              <Box component="span" sx={{ flex: 1 }}>
                Saldo por zona a fecha
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Saldo de cuenta corriente por cliente a la fecha indicada, agrupado por zona, con totales por zona y
                general.
              </Typography>
              <Stack spacing={2} sx={{ mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Movimientos desde"
                    type="date"
                    value={fechaDesdeSaldoZona}
                    onChange={(e) => setFechaDesdeSaldoZona(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Saldo a fecha"
                    type="date"
                    value={fechaHastaListado}
                    onChange={(e) => setFechaHastaListado(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                </Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={incluirMovimientosSaldoZona}
                      onChange={(e) => setIncluirMovimientosSaldoZona(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Detallar movimientos (desmarcado: solo saldo por cliente)"
                />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    onClick={() => void handleGenerarSaldoPorZona()}
                    disabled={loadingSaldoZona || exportingSaldoZona}
                    sx={{ textTransform: 'none' }}
                  >
                    Imprimir
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FileDownloadIcon />}
                    onClick={() => void handleExportSaldoPorZonaExcel()}
                    disabled={exportingSaldoZona || loadingSaldoZona}
                    sx={{ textTransform: 'none' }}
                  >
                    Exportar Excel
                  </Button>
                  {(loadingSaldoZona || exportingSaldoZona) && <CircularProgress size={22} />}
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleCloseListados} sx={{ textTransform: 'none' }}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {saldoZonaPdfUrl ? (
        <PDFViewer
          pdfUrl={saldoZonaPdfUrl}
          onClose={handleCloseSaldoZonaPdf}
          title="Saldo por zona a fecha — Cuentas por cobrar"
          documentId="saldo-por-zona-cxc"
          documentType="informeCliente"
          downloadFileName={`saldo_por_zona_${fechaHastaListado.replace(/-/g, '')}.pdf`}
        />
      ) : null}

      {listadoDeudaPdfUrl ? (
        <PDFViewer
          pdfUrl={listadoDeudaPdfUrl}
          onClose={handleCloseListadoDeudaPdf}
          title="Deuda por vencimiento — Cuentas por cobrar"
          documentId="deuda-por-vencimiento-cxc"
          documentType="informeCliente"
          downloadFileName={`deuda_por_vencimiento_${fechaHastaListado.replace(/-/g, '')}.pdf`}
        />
      ) : null}
    </Box>
  );
};

export default CuentasPorCobrar;
